import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import CalendarIcon from "components/icons/Calendar";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import cx from "classnames";
import { isValidTitle } from "utils/validateTitle";
import { useSpring, animated } from "react-spring";
import { useMeasure } from "react-use";

import loadable from "@loadable/component";
const Contact = loadable(() =>
  import("components/components/molecules/Contact")
);

const IconContainer = styled.div.attrs({
  className:
    "flex justify-center items-center w-30-mattamy h-30-mattamy bg-mattamy-blue inline mr-2 rounded-full text-light-blue",
})``;

const SectionHeading = styled.h3.attrs({
  className: "text-mattamy-blue font-trade-gothic-20 text-3xl mb-1 md:mb-3",
})``;

const ToggleScheduleContainer = styled.div`
  place-self: center;
`;
const getDayHours = (hours) => {
  if (hours) {
    const hoursSplit = hours.split("-");
    if (hoursSplit.length > 0 && hoursSplit[0].length > 1) return hours;
  }
  return "";
};

const hasHoursOrDisplayText = (day) => {
  return day && (day.displayText || getDayHours(day.hours));
};

const getNote = (ssn) => {
  if (ssn && ssn.date) {
    const date = ssn.date.split("-");
    const text = ssn.text;
    if (date && date.length > 2) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const dateNote = new Date(date[0], date[1], date[2]);
      dateNote.setHours(0, 0, 0, 0);
      //console.log({ now, dateNote, date });
      if (dateNote >= now) return text;
    }
  }
  return "";
};

const SalesCenterContact = ({ fields }) => {
  const {
    title,
    scheduleAppointment,
    scheduleTitle,
    city,
    zipcode,
    state,
    stateAbbreviation,
    openNow,
    gmbLink,
  } = fields;
  const firstAddress = getFieldValue(fields, "firstAddress");
  const secondAddress = getFieldValue(fields, "secondAddress");
  const phoneNumberCall = getFieldValue(fields, "phoneNumberCall");
  const phoneNumberMessage = getFieldValue(fields, "phoneNumberMessage");
  const latitude = getFieldValue(fields, "latitude");
  const longitude = getFieldValue(fields, "longitude");
  const schedule = getFieldValue(fields, "schedule") || [];
  const salesCenterNoteVM = getFieldValue(fields, "salesCenterNoteVM");

  const hasSchedule = schedule && schedule.length > 0;
  const defaultHeight = "0px";
  const [contentHeight, setContentHeight] = useState(defaultHeight);
  const [scheduleOpen, toggleScheduleOpen] = useState(false);
  const [scheduleRefHeight, { height }] = useMeasure();
  const getProperHeight = () => {
    if (scheduleOpen) {
      return `${contentHeight}px`;
    }
    return "0px";
  };

  const expand = useSpring({
    height: getProperHeight(),
    pointerEvents: scheduleOpen ? "all" : "none",
  });
  const fade = useSpring({
    opacity: scheduleOpen ? 1 : 0,
    delay: scheduleOpen ? 200 : 0,
  });
  useEffect(() => {
    setContentHeight(height);
    window.addEventListener("resize", setContentHeight(height));

    return window.removeEventListener("resize", setContentHeight(height));
  }, [height]);

  const getClosedHour = () => {
    const weekdays = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const d = new Date();
    let day = d.getDay();
    const hours =
      schedule && schedule.find((h) => h.day.includes(weekdays[day - 1]));
    const chour = hours && hours.hours && hours.hours.split("-");
    return chour && chour.length > 1 ? chour[1] : "";
  };
  const renderSchedule = () => {
    return (
      <animated.div
        ref={scheduleRefHeight}
        style={fade}
        className="flex flex-col items-center text-mattamy-blue justify-end w-full ml-10 mt-1"
      >
        {schedule.map((day) => (
          <li
            className="flex items-center justify-start w-full font-light leading-relaxed"
            key={day.day}
          >
            {hasHoursOrDisplayText(day) && (
              <div className="flex w-full">
                <div className="flex flex-col w-12">
                  <span className="text-left w-10 font-graphie mr-3">
                    {day.day.substring(0, 3) + ":"}
                  </span>
                </div>
                <div className="flex flex-col w-9/12">
                  <span className="text-left font-graphie whitespace-no-wrap">
                    {" " + getDayHours(day.hours)}
                  </span>
                  <span className="text-left font-graphie whitespace-wrap xl:whitespace-no-wrap">
                    {day.displayText ? day.displayText : " "}
                  </span>
                </div>
              </div>
            )}
          </li>
        ))}
      </animated.div>
    );
  };

  if (!firstAddress && !hasSchedule) {
    return null;
  }

  return (
    <div
      className={cx("flex flex-col", {
        "w-full": hasSchedule && firstAddress,
      })}
    >
      {firstAddress && (
        <div className="flex flex-col w-full mb-10 md:mb-12">
          <Contact
            title={title}
            note={getNote(salesCenterNoteVM)}
            firstAddress={firstAddress}
            secondAddress={secondAddress}
            phoneNumberCall={phoneNumberCall}
            phoneNumberMessage={phoneNumberMessage}
            city={city}
            zipcode={zipcode}
            state={state}
            stateAbbreviation={stateAbbreviation}
            latitude={latitude}
            longitude={longitude}
            gmbLink={gmbLink}
          />
        </div>
      )}
      {hasSchedule && schedule.length > 0 && (
        <div className="flex flex-col w-full mb-10 md:mb-12">
          {isValidTitle(scheduleTitle) && (
            <SectionHeading>{scheduleTitle}</SectionHeading>
          )}
          <div className="flex items-center">
            <IconContainer aria-hidden="true" tabIndex={-1}>
              <CalendarIcon className="h-5 w-5" />
            </IconContainer>
            <button
              className="flex justify-center items-center place-self-center"
              onClick={() => {
                toggleScheduleOpen(!scheduleOpen);
              }}
              aria-label={scheduleOpen ? "Close Schedule" : "Show Schedule"}
              aria-expanded={scheduleOpen}
              //as="button"
            >
              <p className="break-all mt-1">
                {openNow ? (
                  <>
                    <span className="font-graphie font-bold text-mattamy-blue">
                      {"Open Now - "}
                    </span>
                    <span className="font-graphie text-mattamy-blue">{"Closes at " + getClosedHour()}</span>
                  </>
                ) : (
                  <span className="font-graphie font-bold text-mattamy-blue">
                    {"Closed Now"}
                  </span>
                )}
              </p>
              <ToggleScheduleContainer className=" place-self-center">
                <svg
                  className={cx("w-6 h-6 text-mattamy-blue transform mt-1", {
                    "rotate-90": scheduleOpen,
                    "-rotate-90": !scheduleOpen,
                  })}
                  viewBox="0 0 24 24"
                  fill="none"
                  onClick={() => {
                    toggleScheduleOpen(!scheduleOpen);
                  }}
                >
                  <path
                    fill="currentColor"
                    d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                  />
                </svg>
              </ToggleScheduleContainer>
            </button>
          </div>
          <div className="w-full  z-20">
            {scheduleOpen && (
              <>
                <animated.div style={expand}>{renderSchedule()}</animated.div>
                <div
                  onFocus={(e) => {
                    //setIsEndOfHours(true);
                  }}
                >
                  {" "}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCenterContact;
